import {
  faArrowUpRightFromSquare,
  faBan,
  faCheckCircle,
  faPen
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ChartingStatus } from '@/types/appointments';
import type { PrerequisiteStatus } from '@/types/scheduleExpectation';
import { Button as ButtonV2, Pill, Tooltip } from '@/ui';
import AppointmentAttendanceStatus from '@/ui/components/AppointmentAttendanceStatus';
import { IconItem } from '@/ui/IconItem/IconItem';
import { CHARTING_STATUS_LABELS, resolveChartingStatus } from '@/utils/chartingStatus';

type Props = {
  prerequisiteStatuses: PrerequisiteStatus[];
  onNavigateToAppointment: (appointmentId: string) => void;
  onNavigateToWaivedExpectation?: (appointmentTypeId: string, cycleId: string) => void;
  onIgnore?: (prereq: PrerequisiteStatus) => void;
  isIgnoring?: boolean;
};

type PillColor = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';

const chartingColor = (status: ChartingStatus, isFuture: boolean): PillColor => {
  if (status === ChartingStatus.SESSION_COMPLETE) return 'primary';
  if (isFuture) return 'neutral';
  if (status === ChartingStatus.SESSION_INCOMPLETE) return 'destructive';
  return 'warning';
};

const PrerequisiteStatuses = ({
  prerequisiteStatuses,
  onNavigateToAppointment,
  onNavigateToWaivedExpectation,
  onIgnore,
  isIgnoring = false
}: Props) => {
  if (prerequisiteStatuses.length === 0) return null;

  const anyMet = prerequisiteStatuses.some((p) => p.met_at !== null || p.waived);
  const visibleStatuses = anyMet
    ? prerequisiteStatuses.filter((p) => p.met_at !== null || p.waived)
    : prerequisiteStatuses;

  const ignoreTarget =
    !anyMet && onIgnore
      ? prerequisiteStatuses.find(
          (p) => p.met_at === null && !p.waived && !/continued/i.test(p.appointment_type_name)
        )
      : undefined;

  const waivedPrereq = anyMet
    ? prerequisiteStatuses.find((p) => p.waived && p.waived_by_expectation_id)
    : undefined;

  return (
    <div className="mb-4">
      <div className="flex items-center gap-2 mb-1">
        <p className="text-sm font-semibold text-gray-700">Prerequisites</p>
        {ignoreTarget && (
          <Tooltip content={`Ignore ${ignoreTarget.appointment_type_name} prerequisite`}>
            <ButtonV2
              size="xs"
              color="warning"
              variant="subtle"
              aria-label={`Ignore prerequisite ${ignoreTarget.appointment_type_name}`}
              disabled={isIgnoring}
              isLoading={isIgnoring}
              onClick={() => onIgnore?.(ignoreTarget)}>
              <FontAwesomeIcon icon={faBan} />
              Ignore
            </ButtonV2>
          </Tooltip>
        )}
        {waivedPrereq && onNavigateToWaivedExpectation && (
          <Tooltip content={`View ignored expectation for ${waivedPrereq.appointment_type_name}`}>
            <ButtonV2
              size="xs"
              color="neutral"
              variant="subtle"
              aria-label={`View ignored expectation for ${waivedPrereq.appointment_type_name}`}
              onClick={() =>
                onNavigateToWaivedExpectation(
                  waivedPrereq.appointment_type_id,
                  waivedPrereq.waived_by_expectation_id ?? ''
                )
              }>
              <FontAwesomeIcon icon={faPen} />
              Ignored
            </ButtonV2>
          </Tooltip>
        )}
      </div>
      {!anyMet && (
        <p className="text-xs text-gray-500 mb-2">
          At least one of the appointments below needs to be attended and completed
        </p>
      )}
      <ul className="space-y-2">
        {visibleStatuses.map((prereq) => {
          const metDate = prereq.met_appointment_start_at
            ? new Date(prereq.met_appointment_start_at).toLocaleDateString([], {
                dateStyle: 'medium'
              })
            : null;

          const iconProps = prereq.waived ? { icon: faCheckCircle, color: 'primary' as const } : {};
          const isAppointmentInFuture =
            prereq.met_appointment_start_at !== null &&
            new Date(prereq.met_appointment_start_at) > new Date();
          const chartingStatus = resolveChartingStatus(
            prereq.met_attendance_charting_status,
            prereq.met_attendance_charting_completed
          );

          return (
            <li
              key={prereq.appointment_type_id}
              className="flex flex-col gap-1"
              aria-label={`${prereq.appointment_type_name}: ${prereq.waived ? 'waived' : prereq.met_at !== null ? 'met' : 'not met'}`}>
              <IconItem checked={prereq.met_at !== null} {...iconProps}>
                <span className="font-medium">{prereq.appointment_type_name}</span>
                {metDate && <span className="text-gray-500 ml-1 text-xs">on {metDate}</span>}
                {prereq.met_appointment_id && (
                  <Tooltip content={`Go to ${prereq.appointment_type_name} appointment`}>
                    <button
                      type="button"
                      aria-label={`Go to ${prereq.appointment_type_name} appointment`}
                      className="ml-1 text-blue-600 text-sm"
                      onClick={() => onNavigateToAppointment(prereq.met_appointment_id ?? '')}>
                      <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                    </button>
                  </Tooltip>
                )}
              </IconItem>
              <div className="flex flex-wrap gap-1 ml-6">
                {prereq.met_attendance_state && (
                  <AppointmentAttendanceStatus status={prereq.met_attendance_state} />
                )}
                {(prereq.met_attendance_charting_completed !== null ||
                  prereq.met_attendance_state !== null) && (
                  <Pill size="sm" color={chartingColor(chartingStatus, isAppointmentInFuture)}>
                    {CHARTING_STATUS_LABELS[chartingStatus]}
                  </Pill>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PrerequisiteStatuses;
